import { useState } from "react";
import { supabase } from "../lib/supabase";
import { useAuth } from "../contexts/AuthContext";
import { Check, Trash2, Plus } from "lucide-react";

export default function Roadmap({ user, isOwner, onUpdate }) {
  const { user: me } = useAuth();
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ title: "", amount: "", note: "" });

  const milestones = user.milestones || [];
  const total   = user.goal_amount   || 0;
  const current = user.current_amount || 0;
  const pct = total > 0 ? Math.min(100, Math.round((current / total) * 100)) : 0;

  const add = async (e) => {
    e.preventDefault();
    if (!form.title) return;
    const newM = {
      user_id:  me.id,
      title:    form.title,
      amount:   parseFloat(form.amount || 0),
      note:     form.note,
      achieved: false,
    };
    const { data, error } = await supabase.from("milestones").insert(newM).select().single();
    if (error) { console.error(error); return; }
    onUpdate({ milestones: [...milestones, data] });
    setShowAdd(false);
    setForm({ title: "", amount: "", note: "" });
  };

  const toggle = async (m) => {
    const newAchieved = !m.achieved;
    const { error } = await supabase.from("milestones").update({ achieved: newAchieved }).eq("id", m.id);
    if (error) { console.error(error); return; }
    // Update current_amount on profile
    const delta = newAchieved ? parseFloat(m.amount || 0) : -parseFloat(m.amount || 0);
    await supabase.from("profiles")
      .update({ current_amount: Math.max(0, current + delta) })
      .eq("id", me.id);
    onUpdate({
      milestones: milestones.map((x) => x.id === m.id ? { ...x, achieved: newAchieved } : x),
      current_amount: Math.max(0, current + delta),
    });
  };

  const remove = async (id) => {
    const target = milestones.find((m) => m.id === id);
    const { error } = await supabase.from("milestones").delete().eq("id", id);
    if (error) { console.error(error); return; }
    const delta = target?.achieved ? -parseFloat(target.amount || 0) : 0;
    if (delta) {
      await supabase.from("profiles")
        .update({ current_amount: Math.max(0, current + delta) })
        .eq("id", me.id);
    }
    onUpdate({
      milestones: milestones.filter((m) => m.id !== id),
      ...(delta ? { current_amount: Math.max(0, current + delta) } : {}),
    });
  };

  return (
    <section className="brutal-card p-5 sm:p-7" data-testid="roadmap">
      <div className="flex items-baseline justify-between gap-3 mb-4 flex-wrap">
        <h2 className="font-display font-extrabold text-2xl sm:text-3xl uppercase tracking-tight">
          The <span className="highlight-marker">Roadmap</span>
        </h2>
        <span className="font-hand text-2xl text-marker-red">— track every dollar</span>
      </div>

      <div className="mb-6">
        <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
          <div className="font-mono text-xs uppercase tracking-widest text-ink/60">Progress</div>
          <div className="font-display font-extrabold">
            ${Number(current).toLocaleString()} / ${Number(total).toLocaleString()}
            <span className="ml-2 font-mono text-sm text-marker-red">{pct}%</span>
          </div>
        </div>
        <div className="border-2 border-ink h-7 bg-white relative overflow-hidden">
          <div
            className="h-full bg-marker-yellow border-r-2 border-ink"
            style={{ width: `${pct}%` }}
            data-testid="progress-bar"
          />
          <div className="absolute inset-0 pointer-events-none"
               style={{ backgroundImage: "repeating-linear-gradient(45deg, transparent 0 6px, rgba(18,18,18,0.08) 6px 7px)" }}
          />
        </div>
      </div>

      <div className="relative roadmap-line pl-12 space-y-5">
        {milestones.length === 0 && (
          <p className="font-hand text-2xl text-ink/60">No milestones yet — add the first one.</p>
        )}
        {milestones.map((m) => (
          <div key={m.id} className="relative" data-testid={`milestone-${m.id}`}>
            <div className={`absolute -left-12 top-1 w-7 h-7 blob border-2 border-ink ${m.achieved ? "bg-marker-red" : "bg-cream"}`} />
            <div className="brutal-card-sm p-4">
              <div className="flex items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="font-display font-extrabold text-lg uppercase tracking-tight">{m.title}</div>
                  {m.amount ? <div className="font-mono text-sm text-marker-blue">+${Number(m.amount).toLocaleString()}</div> : null}
                  {m.note && <div className="font-hand text-xl mt-1">{m.note}</div>}
                </div>
                {isOwner && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => toggle(m)}
                      className={`brutal-btn text-xs ${m.achieved ? "brutal-btn-red" : "brutal-btn-yellow"}`}
                      data-testid={`toggle-milestone-${m.id}`}
                    >
                      <Check size={14} /> {m.achieved ? "Done" : "Mark done"}
                    </button>
                    <button onClick={() => remove(m.id)} className="brutal-btn text-xs" data-testid={`delete-milestone-${m.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}

        {isOwner && !showAdd && (
          <button onClick={() => setShowAdd(true)} className="brutal-btn brutal-btn-blue ml-0" data-testid="add-milestone-btn">
            <Plus size={14} /> Add milestone
          </button>
        )}
        {isOwner && showAdd && (
          <form onSubmit={add} className="brutal-card-sm p-4 space-y-3" data-testid="add-milestone-form">
            <input className="brutal-input" placeholder="Milestone title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="milestone-title-input" />
            <input className="brutal-input" type="number" placeholder="Amount earned (optional)" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="milestone-amount-input" />
            <input className="brutal-input" placeholder="Note / context" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="milestone-note-input" />
            <div className="flex gap-2">
              <button type="submit" className="brutal-btn brutal-btn-yellow" data-testid="save-milestone-btn">Save</button>
              <button type="button" onClick={() => setShowAdd(false)} className="brutal-btn">Cancel</button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}
